/**
 * Currency display helpers for billing UI.
 *
 * Canonical amounts are USD everywhere in the backend; these helpers localize
 * for display using the same Math.round(usdCents * rate) the backend charges
 * with, so displayed amounts always equal what's billed.
 */

export const CURRENCY_SYMBOLS: Record<string, string> = {
  usd: '$',
  gbp: '£',
  eur: '€',
  cad: 'C$',
  aud: 'A$',
  jpy: '¥',
};

export const CURRENCY_LABELS: Record<string, string> = {
  usd: 'USD ($)',
  gbp: 'GBP (£)',
  eur: 'EUR (€)',
  cad: 'CAD ($)',
  aud: 'AUD ($)',
  jpy: 'JPY (¥)',
};

/** Convert a USD amount in cents to the display currency's minor units. */
export function localizeUsdCents(usdCents: number, currency: string, rates: Record<string, number>): number {
  const rate = rates[currency.toLowerCase()] ?? 1; // fail-safe to USD, mirrors backend
  return Math.round(usdCents * rate);
}

/** Format an amount already in the target currency's minor units (no conversion). */
export function formatMinor(minorUnits: number, currency: string): string {
  const symbol = CURRENCY_SYMBOLS[currency.toLowerCase()] ?? '$';
  return `${symbol}${(minorUnits / 100).toFixed(2)}`;
}

/** Convert a USD amount (in cents) to the display currency and format it. */
export function formatUsdCents(usdCents: number, currency: string, rates: Record<string, number>): string {
  return formatMinor(localizeUsdCents(usdCents, currency, rates), currency);
}

/** Convert a USD amount given in dollars (e.g. cloud balances) and format it. */
export function formatUsdDollars(usdDollars: number, currency: string, rates: Record<string, number>): string {
  return formatUsdCents(Math.round(usdDollars * 100), currency, rates);
}
